/**
 * テロップバー (TickerBar) — 画面上部の共通バー。
 *
 * spec: `docs/specs/common/shared-components.md` 「2) テロップバー（TickerBar）」
 *   - AC-SC-010-3: 共通バーに自社の組織名 (eCloud = eAuth 登録の組織名) を固定表示する。
 *     ユーザー個別の ON/OFF は持たない。組織名を取得できない場合は既定の表示名を用いる。
 *   - AC-SC-010-4: 会社ロゴは表示しない (ユーザー単位のロゴ設定機能が無いため)。
 *
 * spec からの意図的逸脱:
 *   - AC-SC-008b (個人設定でテロップ表示 OFF ならバーを非表示) には**従っていない**。
 *     ドッグフーディングでの PdM 指示「ticker は常に表示し、通知が無い状態でも組織名と
 *     日付・時刻は出す」に従い、バー自体は設定に依らず常時表示する (組織名は spec 上も
 *     ユーザー個別 ON/OFF を持たない)。spec 側 AC-SC-008b の更新は PdM レーンの宿題。
 *   - AC-SC-010 (埋め込みモードで非表示) は**未対応**。現状 app に埋め込み用 layout が
 *     存在せず、本 component は MainLayout からのみ描画されるため実害が出ていないだけで、
 *     埋め込み画面 (発電計画内訳等) を実装する際は明示的な抑止が必要。
 *
 * design: `design/js/include-nav.js` の ticker-bar 生成部 (会社名バッジ左端 +
 * 右端 ticker-fixed の 📅日付 / ⏱現在コマ) と `design/css/layout.css` の
 * `.ticker-bar` 系 (高さ 48px / 背景 #0a0a0a / 文字色 #fff / 会社名バッジの右区切り線)。
 *
 * 実装済み: 会社名バッジの右の左端アラートのうち OCCTO 認証情報不備の事業者コード警告
 * (AC-PLC-053、design `#tf-cert-expiry` の `.ticker-alert--left` と同じ枠)。
 * 表示中はバー全体を negative 系背景にする。design の背景明滅は AC-MCC-007
 * 「点滅はさせない」(spec > design) により付けない。クリック遷移も持たない。
 *
 * 本スライスの対象外 (= 未実装):
 *   - テロップ本文のスライド表示 (AC-SC-007b) とカテゴリ別 ON/OFF (AC-SC-009)
 *   - 不整合 / 承認待ち / 証明書期限の左端アラート (複数種になる時は rotator が要る)、
 *     天気、エリアの固定アイテム
 */
import { useTranslation } from 'react-i18next'

import { Flex, HStack, Text } from '@enechain/ecloud-designsystem'
import {
  FaCalendarRegular,
  FaClockRegular,
  FaTriangleExclamationSolid,
} from '@enechain/ecloud-icons'

import { HintTooltip } from '~/components/common/HintTooltip'
import { iconSize } from '~/constants/iconSize'
import { useCurrentSession } from '~/hooks/useCurrentSession'
import { useNowByMinute } from '~/hooks/useNowByMinute'
import { useOcctoCredentialAlerts } from '~/hooks/useOcctoCredentialAlerts'
import { formatTickerDateTime } from '~/models/dateTime'
import { dateToKomaNumber } from '~/models/komaToTime'

/**
 * 組織名の再取得間隔 (5 分)。
 *
 * バーの組織名は「2 組織を別タブで運用する時にどちらを操作中か」を示す唯一の手掛かり
 * (spec AC-SC-010-3 / internalapi session proto のコメント)。起動時 1 回だけの取得だと
 * (a) 起動時の一過性失敗が「組織名未取得」のまま永続化し (b) 長時間開いたタブで
 * セッションが別組織に切り替わっても旧組織名を出し続ける。定期再検証でこの黙った嘘を防ぐ。
 */
const ORGANIZATION_REFETCH_INTERVAL_MS = 5 * 60 * 1000

/**
 * design `design/css/layout.css:894-905` `.ticker-bar { background: #0a0a0a;
 * color: #ffffff; }` の固定配色。ライト / ダークを問わず黒地に白抜きで出す
 * 意図的な固定色で、ecloud semantic token に対応する値が無い (layout.css:929-932 の
 * [mock差異] コメントも「当バーは意図的なダーク固定配色」としてバー内を `#fff` 系に
 * 統一する旨を明記している) ため、design ソース併記で例外化する。
 */
// eslint-disable-next-line no-restricted-syntax -- design/css/layout.css:894-905 .ticker-bar の固定色 (ecloud token 無し)
const BAR_BACKGROUND = '#0a0a0a'
// eslint-disable-next-line no-restricted-syntax -- design/css/layout.css:894-905 .ticker-bar の固定色 (ecloud token 無し)
const BAR_TEXT_COLOR = '#ffffff'
/** design `.ticker-company` の右区切り線 / `.ticker-fixed` の左区切り線。 */
const BAR_DIVIDER_COLOR = 'rgba(255,255,255,0.12)'
/** design `.ticker-bar { height: 48px }`。MainLayout のレイアウト計算とも共有する。 */
export const TICKER_BAR_HEIGHT = '48px'

/**
 * 組織名バッジの最大幅。組織名は eCloud 側で組織管理者が入力する外部データで長さ制約が
 * 無く、`whiteSpace: nowrap` のまま伸ばすと右端の日付・現在コマが視界外へ押し出される。
 * 溢れた分は ellipsis にし、全文は title 属性で読めるようにする。
 */
const ORGANIZATION_BADGE_MAX_WIDTH = '320px'

/**
 * 組織名バッジの最小幅 = **サイドメニュー展開時の幅**。
 *
 * バッジの右端の区切り線と、その真下にあるサイドメニューの右端を揃える
 * (デザイナー指摘 2026-09-29)。組織名が短いとバッジだけが内側で終わり、
 * 縦のラインが途切れて見える。
 *
 * 値は DS `SideMenuLayout` の `WIDTH_SETTINGS`
 * (`packages/components/src/components/SideMenuLayout/constants/constants.ts`) より:
 *   COLLAPSED (アイコンレール) 48px + SECONDARY_EXPANDED (2 枚目のパネル) 200px = 248px
 * = セカンダリメニューを開いた状態の幅。DS 側が変えたらここも合わせる
 * (DS は定数を export していないため、px を写して出典をコメントに残す)。
 *
 * 最大幅 (320px) より小さいので、長い組織名の ellipsis 挙動は変わらない。
 */
const ORGANIZATION_BADGE_MIN_WIDTH = '248px'

export const TickerBar = (): React.ReactElement => {
  const { i18n, t } = useTranslation()
  const now = useNowByMinute()
  const { data, isPending, isError } = useCurrentSession({
    refetchInterval: ORGANIZATION_REFETCH_INTERVAL_MS,
  })

  // AC-SC-010-3: 取得不可 (RPC 失敗 = data なし / eauth に組織名が未登録 = 空文字) でも
  // バッジは消さず既定の表示名を出す。proto3 の非 optional string なので data があれば
  // organizationName は必ず string — 「空文字か否か」の 1 条件で足りる。
  //
  // `isError` のときは `data` を採用しない: TanStack Query は再取得の失敗時に直前の
  // 成功値を保持するため、 data を見ると別組織へ切り替わった直後に再取得が落ちた場合に
  // **旧組織名を出し続ける**。 バーは「今どの組織として操作しているか」の取り違えを
  // 防ぐのが目的なので、 古い組織名より既定の表示名の方が安全側。
  const fetchedName = isError ? '' : (data?.organizationName ?? '')
  // 取得中は「未取得」と紛らわしいので何も出さない (= 起動直後に既定表示名が一瞬見えて
  // から組織名へ差し替わるチラつきも防ぐ)。
  const organizationName = isPending
    ? ''
    : fetchedName !== ''
      ? fetchedName
      : t('common:tickerBar.organizationFallback')

  const slotIndex = dateToKomaNumber(now)

  // AC-PLC-053: 警告が 1 件以上の間だけ左端アラートを出し、バー全体を警告配色にする。
  const { businessCodes: alertCodes } = useOcctoCredentialAlerts()
  const hasAlert = alertCodes.length > 0
  const barTextColor = hasAlert ? 'Text.negativeHigh' : BAR_TEXT_COLOR
  const barBackground = hasAlert ? 'Surface.negativeMid' : BAR_BACKGROUND
  const dividerColor = hasAlert ? 'Border.negativeMid' : BAR_DIVIDER_COLOR

  return (
    <Flex
      alignItems="center"
      flexShrink={0}
      overflow="hidden"
      width="full"
      height={TICKER_BAR_HEIGHT}
      color={barTextColor}
      background={barBackground}
    >
      {/* 会社名バッジ (design `.ticker-company`)。AC-SC-010-4 によりロゴは置かない。 */}
      <HintTooltip
        content={
          organizationName === ''
            ? undefined
            : t('common:tickerBar.organizationTitle', {
                name: organizationName,
              })
        }
      >
        <Flex
          alignItems="center"
          flexShrink={0}
          minWidth={ORGANIZATION_BADGE_MIN_WIDTH}
          maxWidth={ORGANIZATION_BADGE_MAX_WIDTH}
          height="full"
          borderRightWidth="1px"
          borderRightStyle="solid"
          borderRightColor={dividerColor}
          paddingX="4"
          title={
            organizationName === ''
              ? undefined
              : t('common:tickerBar.organizationTitle', {
                  name: organizationName,
                })
          }
        >
          {/* eslint-disable-next-line ebalance/no-adhoc-typography -- design/css/layout.css:933-946 .ticker-company = md/700。md/bold の役割 typography が未定義のため textStyle 化は見送る */}
          <Text
            as="span"
            overflow="hidden"
            fontSize="md"
            fontWeight="bold"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            {organizationName}
          </Text>
        </Flex>
      </HintTooltip>

      {/* 左端アラート (design `.ticker-alert--left`)。明滅・クリック遷移なし。 */}
      {hasAlert && (
        <HStack
          flexShrink={0}
          gap="1"
          minWidth={0}
          color="Text.negativeHigh"
          marginX="4"
          role="alert"
        >
          <FaTriangleExclamationSolid
            boxSize={iconSize.gridInline}
            aria-hidden="true"
          />
          {/* 省略された時の全文は DS Tooltip で出す (UI 辞書 components/tooltip.md。 title は
              HintTooltip の JSDoc にある 2026-08-17 デザイナー既定どおり保険として残す) */}
          <HintTooltip
            content={t('common:tickerBar.occtoCredentialAlert', {
              codes: alertCodes.join(', '),
            })}
          >
            {/* eslint-disable-next-line ebalance/no-adhoc-typography -- design/css/layout.css:1031-1037,1060 .ticker-alert--left = md/600。役割 typography 未定義 */}
            <Text
              as="span"
              overflow="hidden"
              fontSize="md"
              fontWeight="semibold"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              title={t('common:tickerBar.occtoCredentialAlert', {
                codes: alertCodes.join(', '),
              })}
            >
              {t('common:tickerBar.occtoCredentialAlert', {
                codes: alertCodes.join(', '),
              })}
            </Text>
          </HintTooltip>
        </HStack>
      )}

      {/* テロップ本文 (スライド表示) の領域。本スライスでは空のまま確保する。 */}
      <Flex flex="1" minWidth={0} />

      {/* 右端の固定アイテム (design `.ticker-fixed`)。天気 / エリアは対象外。 */}
      <HStack
        flexShrink={0}
        gap="2.5"
        height="full"
        borderLeftWidth="1px"
        borderLeftStyle="solid"
        borderLeftColor={dividerColor}
        paddingX="4"
      >
        <HintTooltip content={t('common:tickerBar.dateTitle')}>
          <HStack gap="1" title={t('common:tickerBar.dateTitle')}>
            <FaCalendarRegular boxSize={iconSize.gridInline} opacity={0.7} />
            <Text as="span" textStyle="badge" whiteSpace="nowrap">
              {formatTickerDateTime(now, i18n.language)}
            </Text>
          </HStack>
        </HintTooltip>
        <HintTooltip content={t('common:tickerBar.komaTitle')}>
          <HStack gap="1" title={t('common:tickerBar.komaTitle')}>
            <FaClockRegular boxSize={iconSize.gridInline} opacity={0.7} />
            <Text as="span" textStyle="badge" whiteSpace="nowrap">
              {t('common:tickerBar.komaValue', { koma: slotIndex })}
            </Text>
          </HStack>
        </HintTooltip>
      </HStack>
    </Flex>
  )
}
